import _, { orderBy, uniqWith } from 'lodash'
import { Instance, SnapshotOut, types } from 'mobx-state-tree'

import { GAME_CONFIG } from '@ppwr/poker'
import { convertUTCDataToUTCDate } from '@ppwr/sdk'

import { extendLogger, LoggerContexts } from '../../utils/logger'
import {
  DayTournamentSchedulesModel,
  DayTournamentSchedulesModelType,
  gameEntityModelPrimitives,
  GameModeTag,
  gameSnapshotModelPrimitives,
  TeachingTableGameModel,
  TeachingTableGameModelType,
  TodayCommunityGamesModel,
  TodayCommunityGamesModelType,
  TournamentAccessControl,
  TournamentDifficulty,
  TournamentModel,
  TeachingTableRosterModel,
  TeachingTableRosterModelType,
  tournamentModelPrimitives,
  TournamentModelType,
  TournamentScheduleModel,
  TournamentScheduleModelType,
  UserGameScheduleResponseModel,
  UserGameScheduleResponseModelType,
  TeachingTableCtaTag,
  selectFromTodayCommunityGames,
  selectFromCommunityGame
} from '../api-store'
import { withRootStore } from '../extensions'

export type TournamentModelSnapshot = SnapshotOut<typeof TournamentModel>

const logger = extendLogger(LoggerContexts.Game)

const todayCommunityGamesSelector = () =>
  selectFromTodayCommunityGames()
    .isFull.isSetReminderSelected.maxPlayersN.minPlayersN.multipleBlindOptions.nAdvancedTables.nBeginnerTables.nowPlaying.scheduleId.status.time.winners.beginnerGames(
      selectFromCommunityGame().actionTime.blinds.channelName.hasLeftGame.isFull.isLocked.name.seats
    )
    .advancedGames(selectFromCommunityGame().actionTime.blinds.channelName.hasLeftGame.isFull.isLocked.name.seats)

/**
 * A TournamentStore model.
 */
export const Tournaments = types
  .model('TournamentModel')
  .props({
    scheduleTournaments: types.optional(types.array(TournamentScheduleModel), []),
    liveCommunityGames: types.optional(types.array(TodayCommunityGamesModel), []),
    scheduledCommunityGames: types.optional(types.array(TodayCommunityGamesModel), []),
    finishedCommunityGames: types.optional(types.array(TodayCommunityGamesModel), []),
    scheduleTournamentsGroupedByDay: types.optional(types.array(DayTournamentSchedulesModel), []),
    currentGameWithTournament: types.maybe(TournamentModel),
    yourLiveGames: types.optional(types.array(UserGameScheduleResponseModel), []),
    yourScheduledGames: types.optional(types.array(UserGameScheduleResponseModel), []),
    teachingTableScheduleGames: types.optional(types.array(TeachingTableGameModel), []),
    teachingTableLiveGames: types.optional(types.array(TeachingTableGameModel), []),
    teachingTableRoster: types.maybe(TeachingTableRosterModel)
  })
  .extend(withRootStore)
  .views((self) => ({
    get currentTournament(): TournamentModelType | undefined {
      // return self.rootStore.game?.tournament
      return self.currentGameWithTournament
    },
    get todayCommunityGames(): TodayCommunityGamesModelType[] {
      return [...self.finishedCommunityGames, ...self.liveCommunityGames, ...self.scheduledCommunityGames]
    },
    get yourGamesAreEmpty() {
      return self.yourLiveGames.length === 0 && self.yourScheduledGames.length === 0
    },
    get allDataTeachingTable(): TeachingTableGameModelType[] {
      return [...self.teachingTableLiveGames, ...self.teachingTableScheduleGames]
    },
    get scheduledTeachingTables(): TeachingTableGameModelType[] {
      // Returns all teaching tables scheduled where the user is considered to be enrolled in. To check that we use
      // the cta tag. If the tag is enrolled, viewRoster or joinTable that means the user is enrolled.
      return [...self.teachingTableLiveGames, ...self.teachingTableScheduleGames].filter(
        (x) =>
          x.cta?.tag === TeachingTableCtaTag.enrolled ||
          x.cta?.tag === TeachingTableCtaTag.viewRoster ||
          x.cta?.tag === TeachingTableCtaTag.joinTable
      )
    }
  }))
  .views((self) => ({
    //NOTE - Currently same variables are used for quick and tour types and if we make getter only for tournaments and need to put checks for quick and tour
    get startingChips() {
      if (self.rootStore.game.gameKind === GameModeTag.threeCard) return GAME_CONFIG.THREE_CARD.STARTING_CHIPS
      if (self.currentTournament) return self.currentTournament?.startingChips
      return 10000
    },
    get blindTime(): number {
      const blindTimer = self.currentTournament?.blindTimer ? self.currentTournament.blindTimer * 60 : 300
      return blindTimer
    },
    get tournamentAccessControl(): string {
      return self.currentTournament?.accessControl ? self.currentTournament.accessControl : ''
    },
    get scheduledTournaments() {
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      const todaySchedules = self.scheduleTournaments.filter((s) => s.startDayOfWeekUTC === today.getDay())
      const sortedTodaySchedules = orderBy(
        todaySchedules,
        ['startDayOfWeekUTC', 'startHoursUTC', 'startMinutesUTC'],
        ['asc', 'asc', 'asc']
      )

      const tomorrowSchedules = self.scheduleTournaments.filter((s) => s.startDayOfWeekUTC === tomorrow.getDay())
      const sortedTomorrowSchedules = orderBy(
        tomorrowSchedules,
        ['startDayOfWeekUTC', 'startHoursUTC', 'startMinutesUTC'],
        ['asc', 'asc', 'asc']
      )

      return [sortedTodaySchedules, sortedTomorrowSchedules]
    },
    get tournamentName() {
      const name = self.currentTournament?.name ? self.currentTournament.name : ''
      return name
    }
  }))
  .actions((self) => ({
    clearCurrentTournament() {
      self.currentGameWithTournament = undefined
    },
    setCurrentTournament(tournament: TournamentModelType) {
      const clonedTournament = _.cloneDeep(tournament)
      self.currentGameWithTournament = TournamentModel.create({ ...clonedTournament })
    },
    setLiveCommunityGames(communityGames: TodayCommunityGamesModelType[]) {
      self.liveCommunityGames.replace(communityGames)
    },
    setScheduledCommunityGames(communityGames: TodayCommunityGamesModelType[]) {
      self.scheduledCommunityGames.replace(communityGames)
    },
    setYourLiveGames(games: UserGameScheduleResponseModelType[]) {
      self.yourLiveGames.replace(games)
    },
    setYourScheduledGames(games: UserGameScheduleResponseModelType[]) {
      self.yourScheduledGames.replace(games)
    },
    setTeachingTableScheduleGames(teachingTable: TeachingTableGameModelType[]) {
      self.teachingTableScheduleGames.replace(teachingTable)
    },
    setTeachingTableLiveGames(teachingTable: TeachingTableGameModelType[]) {
      self.teachingTableLiveGames.replace(teachingTable)
    },
    setTeachingTableRoster(teachingTableRoster: TeachingTableRosterModelType | undefined) {
      self.teachingTableRoster = teachingTableRoster
    },
    setFinishedCommunityGames(communityGames: TodayCommunityGamesModelType[]) {
      self.finishedCommunityGames.replace(communityGames)
    },
    setScheduleTournaments(value: TournamentScheduleModelType[]) {
      // Convert api schedules to local time
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const dayAfterTomorrow = new Date(tomorrow)
      dayAfterTomorrow.setDate(tomorrow.getDate() + 1)

      const uniqueSchedules = uniqWith(
        value,
        (a, b) =>
          a.startDayOfWeekUTC === b.startDayOfWeekUTC &&
          a.startHoursUTC === b.startHoursUTC &&
          a.startMinutesUTC === b.startMinutesUTC
      )

      // DEBUG: log timezone data
      // console.log('tz getDays', today.getDay(), tomorrow.getDay(), dayAfterTomorrow.getDay())

      const localSchedules = uniqueSchedules.map((s) => {
        // Convert UTC time to local time in user's time zone
        const localTime = convertUTCDataToUTCDate(
          s.startDayOfWeekUTC ?? 0,
          s.startHoursUTC ?? 0,
          s.startMinutesUTC ?? 0
        )

        const dayOfWeekUTC = s.startDayOfWeekUTC ?? 0
        // Since UTC days will span 2 days in some timezones, we need to adjust the day of week accordingly
        const startDayOfWeekUTC =
          localTime.getUTCDay() !== localTime.getDay() ? (dayOfWeekUTC - 1 < 0 ? 6 : dayOfWeekUTC - 1) : dayOfWeekUTC

        // DEBUG: log timezone data
        // console.log('tz', s.name, s.startDayOfWeekUTC, localTime.getUTCDay(), localTime.getDay(), startDayOfWeekUTC)

        const local24Time = localTime.toLocaleTimeString([], {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        })
        const locaStartHour = parseInt(local24Time.split(':')[0]) ?? 0

        return {
          ...s,
          startDayOfWeekUTC,
          startHoursUTC: locaStartHour,
          scheduleStartTime: localTime.toLocaleTimeString([], {
            hour12: true,
            hour: '2-digit',
            minute: '2-digit'
          })
        }
      })

      self.scheduleTournaments.replace(localSchedules)
    },
    setScheduleTournamentsGroupedByDay(value: DayTournamentSchedulesModelType[]) {
      //console.log(value)

      // DEBUG: log timezone data
      // console.log('tz getDays', today.getDay(), tomorrow.getDay(), dayAfterTomorrow.getDay())

      const localSchedulesGroupedByDay = value.map((sch) => {
        const localSchedules = sch.schedules?.map((s) => {
          // Convert UTC time to local time in user's time zone
          const localTime = convertUTCDataToUTCDate(
            s.startDayOfWeekUTC ?? 0,
            s.startHoursUTC ?? 0,
            s.startMinutesUTC ?? 0
          )

          const dayOfWeekUTC = s.startDayOfWeekUTC ?? 0
          // Since UTC days will span 2 days in some timezones, we need to adjust the day of week accordingly
          const startDayOfWeekUTC =
            localTime.getUTCDay() !== localTime.getDay() ? (dayOfWeekUTC - 1 < 0 ? 6 : dayOfWeekUTC - 1) : dayOfWeekUTC

          // DEBUG: log timezone data
          // console.log('tz', s.name, s.startDayOfWeekUTC, localTime.getUTCDay(), localTime.getDay(), startDayOfWeekUTC)
          const local24Time = localTime.toLocaleTimeString([], {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit'
          })
          const locaStartHour = parseInt(local24Time.split(':')[0]) ?? 0

          return {
            ...s,
            startDayOfWeekUTC,
            startHoursUTC: locaStartHour,
            scheduleStartTime: localTime.toLocaleTimeString([], {
              hour12: true,
              hour: '2-digit',
              minute: '2-digit'
            })
          }
        })

        // const sortedSchedules = orderBy(
        //   localSchedules,
        //   ['startDayOfWeekUTC', 'startHoursUTC', 'startMinutesUTC'],
        //   ['asc', 'asc', 'asc']
        // )

        return {
          ...sch,
          schedules: localSchedules
        }
      })

      const days = [0, 1, 2, 3, 4, 5, 6]

      const finalSchedules: DayTournamentSchedulesModelType[] = []

      days.forEach((day) => {
        const result: TournamentScheduleModelType[] = []

        localSchedulesGroupedByDay.forEach((sch) => {
          const group =
            sch.schedules?.filter((s) => s.startDayOfWeekUTC === day /*getSchedulesForDay(s as Sched, day)*/) ?? []

          result.push(...(group as TournamentScheduleModelType[]))
        })

        // localSchedulesGroupedByDay.forEach((sch) => {
        //   const group = sch.schedules?.filter((s) => getSchedulesForDay(s as Sched, day)) ?? []

        //   result.push(...(group as TournamentScheduleModelType[]))
        // })

        //TODO: fix correct order for a day whose in wednesday but on my local time is a day before
        // take a look at 64f23db9631526f6e30dcfe3 it is on day 3 but locally for me it is day 2
        //const sortedSchedules = orderBy(result, ['startHoursUTC', 'startMinutesUTC'], ['asc', 'asc'])

        const sortedSchedules = orderBy(
          result,
          ['startDayOfWeekUTC', 'startHoursUTC', 'startMinutesUTC'],
          ['asc', 'asc', 'asc']
        )

        finalSchedules.push({
          schedules: sortedSchedules
        } as unknown as DayTournamentSchedulesModelType)
      })

      //console.log(localSchedulesGroupedByDay)
      //console.log(finalSchedules)

      self.scheduleTournamentsGroupedByDay.replace(finalSchedules)
    }
  }))
  .actions((self) => ({
    async getTournament({ channelName }: { channelName: string }) {
      const response = await self.rootStore.api.queryGetTournamentByChannelName(
        { channelName },
        () => gameEntityModelPrimitives.tournament(tournamentModelPrimitives),
        {
          fetchPolicy: 'no-cache'
        }
      )

      if (!response.getTournamentByChannelName.tournament) {
        throw new Error('Missing Tournament Info')
      }

      self.clearCurrentTournament()
      self.setCurrentTournament(response.getTournamentByChannelName.tournament as TournamentModelType)
    },
    async getActiveTournament(accessControl?: TournamentAccessControl, difficulty?: TournamentDifficulty | null) {
      try {
        const response = await self.rootStore.api.queryGetTournaments(
          {
            accessControl,
            difficulty
          },
          (qb) => gameEntityModelPrimitives.snapshot(gameSnapshotModelPrimitives).tournament(tournamentModelPrimitives),
          { fetchPolicy: 'no-cache' }
        )
        return response.getTournaments
      } catch (error) {
        logger.error('getActiveTournament API Error: ', error)
        throw error
      }
    },
    async getActivePublicTournament() {
      try {
        const response = await self.rootStore.api.mutateFindOrCreatePublicTournament({}, (qb) =>
          gameEntityModelPrimitives.snapshot(gameSnapshotModelPrimitives).tournament(tournamentModelPrimitives)
        )

        if (!response.findOrCreatePublicTournament.tournament) {
          throw new Error('Missing Tournament info')
        }
        self.clearCurrentTournament()
        self.setCurrentTournament(response.findOrCreatePublicTournament.tournament as TournamentModelType)
        const game = response.findOrCreatePublicTournament
        const tournament = game.tournament
        const channelName = game.channelName ?? ''
        if (!channelName) {
          throw new Error('Missing channelName')
        }

        if (!tournament) {
          throw new Error('Missing tournament information')
        }

        self.rootStore.game.setGameKind(GameModeTag.tournament)
        self.rootStore.game.setRemainingTime(
          tournament.blindTimer ? tournament.blindTimer * 60 : self.rootStore.game.blindTime
        )
        self.rootStore.game.setActionTime(
          tournament.actionTime ? tournament.actionTime * 1000 : self.rootStore.game.actionTime
        )
        self.rootStore.communication.joinChannel(channelName)
        await self.rootStore.game.setupGame()
      } catch (error) {
        logger.error('getActiveTournament API Error: ', error)
        throw error
      }
    },
    async getTournamentStats() {
      try {
        const response = await self.rootStore.api.queryGetTournamentStats(
          { tournamentId: self.rootStore.game.gameId ?? '' },
          'players {playerName playerId stack chips isLeft isRemovedByTeacher state} runTime winners isGameRunning',
          { fetchPolicy: 'no-cache' }
        )
        logger.info('Tournament Stats ', response)
        self.rootStore.game.setLeaderboardData(response.getTournamentStats)
      } catch (error) {
        console.log('Error in fetching the tournament stats', error)
      }
    },
    async getScheduledTournaments() {
      try {
        const currentDay = new Date().getDay()
        const response = await self.rootStore.api.queryGetScheduledTournaments(
          { currentDay },
          '_id actionTime blindTimer createdAt difficulty name notificationTargetGroup scheduleStartTime startDayOfWeekUTC startHoursUTC startMinutesUTC startingChips updatedAt',
          { fetchPolicy: 'no-cache' }
        )
        self.setScheduleTournaments(response.getScheduledTournaments)
      } catch (error) {
        logger.error('getScheduledTournaments API Error: ', error)
        throw error
      }
    },
    async getTournamentSchedulesGroupedByDay() {
      try {
        const currentDay = new Date().getDay()
        const response = await self.rootStore.api.queryGetTournamentSchedules(
          { currentDay },
          `schedules {
          _id
          actionTime
          blindTimer
          createdAt
          difficulty
          name
          notificationTargetGroup
          startDayOfWeekUTC
          startHoursUTC
          startMinutesUTC
          startingChips
          usersToNotify
        }`,
          { fetchPolicy: 'no-cache' }
        )
        self.setScheduleTournamentsGroupedByDay(response.getTournamentSchedules)
      } catch (error) {
        logger.error('getScheduledTournaments API Error: ', error)
        throw error
      }
    },
    async updateUsersToNotifyForSchedule(scheduleId: string) {
      const userId = self.rootStore.session.user?._id

      if (userId) {
        await self.rootStore.api.mutateUpdateUsersToNotifyForSchedule({ scheduleId, userId })
      }
    },
    async enableDefaultNotificationSchedule() {
      const userId = self.rootStore.session.user?._id

      if (userId) {
        await self.rootStore.api.mutateEnableDefaultNotification({ userId })
      }
    },
    async getCurrentTournament(channelName: string) {
      if (channelName) {
        try {
          await self.rootStore.game.getTournament({ channelName })
        } catch (error) {
          logger.error('getCurrentTournament API Error: ', error)
          throw error
        }
      }
    },
    async joinLiveGame(channelName?: string) {
      const {
        communication,
        game: { getCurrentTournament, gameId },
        telemetry,
        session
      } = self.rootStore
      try {
        if (channelName && session.user) {
          communication.joinChannel(channelName)
          await getCurrentTournament(channelName)
          await telemetry.setUser({ ...session.user, gameId: gameId })
        }
      } catch (e) {
        logger.error(`Error in JoinLiveGame ${e}`)
      }
    },
    async getLiveCommunityGames(signal?: AbortSignal | null): Promise<void> {
      try {
        const result = await self.rootStore.api.queryGetLiveCommunityGames(undefined, todayCommunityGamesSelector, {
          fetchPolicy: 'no-cache',
          httpClientOptions: {
            // @ts-ignore
            signal
          }
        })
        self.setLiveCommunityGames(result.getLiveCommunityGames)
      } catch (e) {
        logger.error('queryGetLiveCommunityGames API Error: ', e)
        throw e
      }
    },
    async getScheduledCommunityGames(signal?: AbortSignal | null): Promise<void> {
      try {
        const result = await self.rootStore.api.queryGetScheduledCommunityGames(
          undefined,
          todayCommunityGamesSelector,
          {
            fetchPolicy: 'no-cache',
            httpClientOptions: {
              // @ts-ignore
              signal
            }
          }
        )

        self.setScheduledCommunityGames(result.getScheduledCommunityGames)
      } catch (e) {
        logger.error('getScheduledCommunityGames API Error: ', e)
        throw e
      }
    },
    async getTodayFinishedCommunityGames(signal?: AbortSignal | null): Promise<void> {
      try {
        const result = await self.rootStore.api.queryGetTodayFinishedCommunityGames(
          undefined,
          todayCommunityGamesSelector,
          {
            fetchPolicy: 'no-cache',

            httpClientOptions: {
              // @ts-ignore
              signal
            }
          }
        )

        self.setFinishedCommunityGames(result.getTodayFinishedCommunityGames)
      } catch (e) {
        logger.error('queryGetTodayFinishedCommunityGames API Error: ', e)
        throw e
      }
    },
    async getYourLiveGames(signal?: AbortSignal | null): Promise<void> {
      try {
        const result = await self.rootStore.api.queryQueryUserGameLiveByUserId(undefined, undefined, {
          fetchPolicy: 'no-cache',
          httpClientOptions: {
            // @ts-ignore
            signal
          }
        })

        self.setYourLiveGames(result.queryUserGameLiveByUserId)
      } catch (e) {
        logger.error('queryQueryUserGameLiveByUserId API Error: ', e)
        throw e
      }
    },
    async getYourScheduledGames(signal?: AbortSignal | null): Promise<void> {
      try {
        const result = await self.rootStore.api.queryQueryUserGameScheduleByUserId(undefined, undefined, {
          fetchPolicy: 'no-cache',
          httpClientOptions: {
            // @ts-ignore
            signal
          }
        })
        self.setYourScheduledGames(result.queryUserGameScheduleByUserId)
      } catch (e) {
        logger.error('querygetYourScheduledGames API Error: ', e)
        throw e
      }
    },

    async getTeachingTableLiveGames(): Promise<void> {
      try {
        const result = await self.rootStore.api.queryGetLiveTeachingTableGames(
          undefined,
          `channelName
          isFull
          scheduleId
          status
          tableAmount
          scheduleTime
          winners
          openSeats
          teachingTable {
            name
            active
            observers
            players
            price
            stripePriceId
            stripeProductId
            teacherId
            teacherName
            teacherAvatar
          }
          cta {
            ctaText
            tag
          }`,
          {
            fetchPolicy: 'no-cache',
            httpClientOptions: {
              // @ts-ignore
            }
          }
        )

        self.setTeachingTableLiveGames(result?.getLiveTeachingTableGames || [])
      } catch (e) {
        logger.error('queryGetLiveTeachingTableGames API Error: ', e)
        throw e
      }
    },

    async getTeachingTableScheduleGames(): Promise<void> {
      try {
        const result = await self.rootStore.api.queryGetScheduledTeachingTableGames(
          undefined,
          `
          channelName
          isFull
          scheduleId
          status
          tableAmount
          scheduleTime
          winners
          openSeats
          teachingTable {
            name
            active
            observers
            players
            price
            stripePriceId
            stripeProductId
            teacherId
            teacherName
            teacherAvatar
          }
          cta {
            ctaText
            tag
          }
          `,

          {
            fetchPolicy: 'no-cache',
            httpClientOptions: {
              // @ts-ignore
            }
          }
        )

        self.setTeachingTableScheduleGames(result?.getScheduledTeachingTableGames || [])
      } catch (e) {
        logger.error('queryGetScheduledTeachingTableGames API Error: ', e)
        throw e
      }
    },

    async getTeachingTablesRoster(scheduledAt: string, teachingTableId: string | ''): Promise<void> {
      try {
        const result = await self.rootStore.api.queryGetTeachingTableRoster(
          { scheduledAt, teachingTableId },
          `
          players {
            email
            name
            avatar
          }
          observers {
            email
            name
            avatar
          }
          `,
          {
            fetchPolicy: 'no-cache',
            httpClientOptions: {
              // @ts-ignore
            }
          }
        )
        self.setTeachingTableRoster(result.getTeachingTableRoster)
      } catch (e) {
        logger.error('querygetYourScheduledGames API Error: ', e)
        throw e
      }
    }
  }))

/**
 * The TournamentStore instance.
 */
export type TournamentsStore = Instance<typeof Tournaments>

/**
 * The data of a TournamentStore.
 */
export type TournamentStoreSnapshot = SnapshotOut<typeof Tournaments>
